import { useState } from 'react';
import { Link } from 'react-router-dom';
import { describeTerms } from '@affily/shared/commission';
import { formatCents, formatCount, formatPercent, formatEpc } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { PartnershipGifts } from '../../components/PartnershipGifts.jsx';
import { TrackingLink } from '../../components/TrackingLink.jsx';
import { CreatorCode } from '../../components/CreatorCode.jsx';
import { PartnershipActivity } from '../../components/PartnershipActivity.jsx';
import { PartnershipPosts } from '../../components/PartnershipPosts.jsx';
import { Button, Badge, Spinner, EmptyState, Alert, Unknown } from '../../components/ui.jsx';

const statusTone = {
  active: 'positive',
  paused: 'caution',
  completed: 'neutral',
  terminated: 'negative',
};

export default function Partnerships({ side }) {
  const { data, loading, reload } = useFetch(`/partnerships?side=${side}`);
  // The creator's own gift requests; a brand handles theirs on the Gifts page.
  const gifts = useFetch('/gift-requests?side=creator', { skip: side !== 'creator' });
  const giftRequests = gifts.data?.giftRequests ?? [];
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const partnerships = data?.partnerships ?? [];

  async function end(id) {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/partnerships/${id}/end`, { status: 'completed' });
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <DashboardLayout
      side={side}
      title="Partnerships"
      description="Terms here are the ones frozen at acceptance, not the program’s current listing."
    >
      <Alert className="mb-4">{error}</Alert>

      {loading ? (
        <Spinner />
      ) : partnerships.length === 0 ? (
        <EmptyState title="No partnerships yet">
          {side === 'brand'
            ? 'Accept an application and a tracking link is issued automatically.'
            : 'Apply to a program, and your tracking link appears here once you’re accepted.'}
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {partnerships.map((p) => (
            <article key={p.id} className="rounded-lg border border-rule bg-paper-raised">
              <header className="flex flex-wrap items-start justify-between gap-4 border-b border-rule p-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    {p.program ? (
                      <Link
                        to={`/programs/${p.program.slug}`}
                        className="font-medium text-ink hover:text-accent"
                      >
                        {p.program.title}
                      </Link>
                    ) : (
                      <span className="font-medium text-ink-muted">Program removed</span>
                    )}
                    <Badge tone={statusTone[p.status]}>{p.status}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-ink-muted">
                    {side === 'brand'
                      ? p.creator?.displayName ?? 'Creator removed'
                      : p.brand?.companyName ?? ''}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-semibold tabular-nums text-accent">
                    {describeTerms(p.terms)}
                  </p>
                  <p className="text-xs text-ink-faint">
                    {p.terms.cookieWindowDays}-day cookie · frozen{' '}
                    {new Date(p.terms.snapshotAt).toLocaleDateString()}
                  </p>
                </div>
              </header>

              <div className="grid grid-cols-2 gap-px bg-rule sm:grid-cols-6">
                {[
                  ['Clicks', formatCount(p.stats.uniqueClicks), `${formatCount(p.stats.totalClicks)} total`],
                  ['Conversions', formatCount(p.stats.conversions), null],
                  [
                    'CVR',
                    p.stats.uniqueClicks
                      ? formatPercent((p.stats.conversions / p.stats.uniqueClicks) * 100)
                      : null,
                    null,
                  ],
                  [
                    'EPC',
                    p.stats.uniqueClicks
                      ? formatEpc(p.stats.commissionGrossCents / 100 / p.stats.uniqueClicks, { currency: p.terms?.currency })
                      : null,
                    null,
                  ],
                  ['Revenue', formatCents(p.stats.revenueCents, { currency: p.terms?.currency }), null],
                  [
                    side === 'brand' ? 'Commission owed' : 'Earned (net)',
                    formatCents(side === 'brand' ? p.stats.commissionGrossCents : p.stats.commissionNetCents, {
                      currency: p.terms?.currency,
                    }),
                    null,
                  ],
                ].map(([label, value, hint]) => (
                  <div key={label} className="bg-paper-raised px-4 py-3">
                    <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                      {label}
                    </p>
                    <p className="mt-0.5 font-medium tabular-nums">
                      {value ?? <Unknown reason="No clicks yet" />}
                    </p>
                    {hint && <p className="text-xs text-ink-faint">{hint}</p>}
                  </div>
                ))}
              </div>

              {p.promoCode && (
                <div className="border-t border-rule p-4">
                  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                    Creator code
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <CreatorCode
                      partnership={p}
                      side={side}
                      onError={setError}
                      onChanged={reload}
                    />
                  </div>
                  {side === 'creator' && (
                    <p className="mt-1.5 text-xs text-ink-faint">
                      For podcasts, video and anywhere people can’t click: a sale using this code
                      is credited to you, no link needed.
                    </p>
                  )}
                </div>
              )}

              {p.trackingLinks?.length > 0 && (
                <div className="border-t border-rule p-4">
                  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                    Tracking links
                  </p>
                  <ul className="mt-2 space-y-2">
                    {p.trackingLinks.map((l) => (
                      <li key={l.id} className="flex flex-wrap items-center gap-2">
                        <TrackingLink url={l.url} onError={setError} />
                        {l.label && <span className="text-xs text-ink-faint">{l.label}</span>}
                        <span className="text-xs text-ink-faint">
                          {formatCount(l.uniqueClicks)} unique · {formatCount(l.totalClicks)} total
                        </span>
                        {!l.isActive && <Badge tone="neutral">inactive</Badge>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {side === 'creator' && p.terms.commissionType === 'flat_per_post' && (
                <PartnershipPosts partnership={p} />
              )}

              {side === 'creator' && <PartnershipActivity partnershipId={p.id} />}

              {side === 'creator' && (
                <PartnershipGifts partnership={p} requests={giftRequests} onChanged={gifts.reload} />
              )}

              {p.status === 'active' && (
                <footer className="border-t border-rule px-4 py-2.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => end(p.id)}
                    disabled={busyId === p.id}
                  >
                    Mark completed
                  </Button>
                  <span className="ml-2 text-xs text-ink-faint">
                    Ends tracking. Commissions already earned are unaffected, and completing unlocks
                    reviews both ways.
                  </span>
                </footer>
              )}
            </article>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
